<script lang="ts">
    import Image from '../Image.svelte'
    import type { ImageType } from 'src/types'
    import { PortableText } from '@portabletext/svelte'
    import URL from '../../portableText/URL.svelte'

    export let itemContent : []
    export let itemImage : ImageType = { pngUrl : '' }
    export let itemHeading: string
    export let itemSubCopy: string = ''
    export let open : boolean = false
    export let itemID : string = ''

</script>

<div class="accordion-item">
    <div class="accordion-item__header" >
        {#if itemImage.pngUrl !== ''}<Image pngUrl={itemImage.pngUrl} alt="" className="accordion-item__header__image"/>{/if}
        <div class="accordion-item__header__content">
            <h3>{itemHeading}</h3>
            {#if itemSubCopy}<p>{itemSubCopy}</p>{/if}
        </div>
        <button on:click id={itemID} class="accordion-item__toggle accordion-item__toggle--desktop" class:accordion-item__toggle--rotate={open}></button>
    </div>
    <div class="accordion-item__content" class:accordion-item--expanded={open}>
        {#each itemContent as content}
            {#if content.show_heading}<h4>{content.heading}</h4>{/if}
            <PortableText 
                value={content.paragraph}
                components={{marks: {link: URL}}}
            />
        {/each}
    </div>
    <button on:click id={itemID} class="accordion-item__toggle accordion-item__toggle--mobile" class:accordion-item__toggle--rotate={open}></button>
</div>

<style lang="postcss">

    @import '../../../styles/_mixins.css';
    @mixin fadeButton;

    .accordion-item {

        border:solid;
        border-width:0 0 2px;
        border-color:rgba(#2f96f4, .40);
        height:100%;
        padding:0px 0 0px;
        margin:0 0 40px;
        overflow:hidden;
        transition: max-height 0.7s ease-in;
        position: relative;
        display:flex;
        flex-direction: column;
        align-items: center;

        & .accordion-item__header {
            display: flex;
            align-items:center;
            justify-content:flex-end;
            width:100%;
            gap:0 16px;
            max-height:80px;

            @mixin screen-sm {
                max-height:100px;
            }

            & .accordion-item__header__content {
                flex:60%;

                & h3 {
                    @mixin heading-h3;
                    font-size:22px;
                    font-weight:700;
                    margin:0px;

                    @mixin screen-sm {
                        margin:0 0 8px;
                        font-size:20px;
                    }
                }

                & p {
                    font-size:16px;
                    line-height: 150%;
                    margin:0;
                }
            }


            & :global(.accordion-item__header__image) {

                max-width: 70px;

                @mixin screen-sm {
                    display:none;
                }
            }
        }

        & .accordion-item__content {
            padding:40px 0 0px;
            max-height:0px;
            overflow: hidden;
            transition: max-height .85s;
            align-self: flex-start;

            & h4 {
                margin:0 0 8px;
                font-size:18px;
                font-weight:700;
            }

            & :global(strong) {
                font-weight:600;
            }

            & :global(p) {
                @mixin paragraph;
                margin:0 0 24px;
            }

            & :global(p:last-child) {
                margin-bottom:40px;
            }

            & :global(ul) {
                display: flex;
                flex-direction: column;
                margin:0 0 40px !important;

            }

            & :global(a) {
                font-weight:600;
                color:var(--color_offerzen_cerulean);
                text-decoration: none;
                position: relative;
                white-space: break-spaces;

                & :global(img) {
                    position: absolute;
                    bottom:3px;
                    right:-20px;
                }

                &:hover {
                    color:var(--color_offerzen_cerulean);
                }

                &:hover > :global(img) {
                    transition: transform .7s;
                    transform: translateX(3px);
                }

                @mixin screen-sm {
                    text-decoration:underline;

                    &:active {
                        color:var(--color_offerzen_cerulean);
                    }
                }
            }

        }

        & .accordion-item__toggle {
            width: 40px;
            height: 40px;
            background-color: #2f96f4;
            border: 2px solid #0e204b;
            border-radius: 50%;
            justify-content: center;
            align-items: center;
            transition: all .2s ease-in;
            display: flex;
            box-shadow: inset -3px -2px rgba(0,0,0,.25), inset 2px 4px rgba(255,255,255,.25);

            @mixin screen-sm {
                margin: 0 0 24px;
            }

            &::after {
                content: "arrow";
                color:transparent;
                display: flex;
                align-items:center;
                justify-content:center;
                background-image: url('/assets/cheveron-down.png');
                height:20px;
                width:20px;
                -webkit-background-size: auto 100%;
                -moz-background-size: auto 100%;
                -o-background-size: auto 100%;
                background-size: auto 100%;
                background-repeat:no-repeat;
                pointer-events: initial; /* keep it for the pseudo element */
            }
        }

        & .accordion-item__toggle--desktop {
            @mixin screen-sm {
                display:none;
            }
        }

        & .accordion-item__toggle--mobile {
            display:none;
            @mixin screen-sm {
                display:flex;
            }
        }

    }


    .accordion-item__toggle--rotate {
        transform: rotate(180deg);
        box-shadow: inset 3px 2px rgba(0,0,0,.25), inset -2px -4px rgba(255,255,255,.25) !important;
    }

    .accordion-item--extend {
        @mixin screen-sm {
            max-height:110px;
        }
    }

    .accordion-item--expanded {
        max-height: 1000px !important;
        display:block !important;

        @mixin screen-sm {
            max-height:1500px !important;
        }
    }

    .theme-dark {
        color: var(--color_offerzen_lightblue_secondary);
        border-radius:12px;
        background-color:#0e253d;
        padding:24px 24px 0px 24px;
        border:none;
        margin:0 0 24px;

        @mixin screen-sm {
            margin: 0 0 40px;
        }

        & .accordion-item__header__content {
            & h3 {
               color:var(--color_white);
            }

            color:var(--color_offerzen_lightblue_secondary);
        }

        & .accordion-item__content {
            padding:24px 0 0;
            width:100%;
            line-height:150%;
            color:var(--color_offerzen_lightblue_secondary);

            & :global(a) {
                font-weight:600;
                color:var(--color_offerzen_lightblue_secondary);
            }

            & :global(p) {
                @mixin screen-sm {
                    width:90%;
                }
            }

            & :global(p:last-child) {
                margin-bottom:24px;
            }
        }
    }
</style>